import type { AccessorFn } from '@tanstack/vue-table'
import { defu } from 'defu'
import type { CustomColumnMeta } from '../types/tanstack-table'

export type MultiLineMetaOptions = Pick<CustomColumnMeta, 'align' | 'className'>

/**
 * Options for creating standard table columns with consistent formatting
 */
export interface StandardColumnOptions<T>
  extends Omit<CustomColumnMeta, 'verticalAlign'> {
  /** Unique identifier for the column */
  id: string
  /** Display header text for the column */
  header: string
  /** Data accessor - property key or function to extract cell value */
  accessor: keyof T | AccessorFn<T, unknown>
  /** Fixed width of the column in pixels */
  size?: number
  /** Whether the column can be resized */
  enableResizing?: boolean
  /** Whether the column can be sorted */
  enableSorting?: boolean
}

/**
 * Helper function to create consistent meta objects for table columns
 * @param options - Configuration options for the column meta
 * @returns Column meta object with default values merged
 */
export const createColumnMeta = (options: Partial<CustomColumnMeta> = {}) =>
  defu(options, { align: 'left' } as Partial<CustomColumnMeta>)

/**
 * Helper function to create meta objects for multi-line columns
 * @param options - Configuration options for multi-line columns
 * @returns Column meta object configured for multi-line content with vertical alignment set to 'start'
 */
export const createMultiLineMeta = (options: MultiLineMetaOptions = {}) =>
  createColumnMeta({
    align: options.align,
    className: options.className,
    verticalAlign: 'start',
  })
